import React, { useEffect, useMemo, useState } from 'react';
import { FaSun, FaMoon, FaArrowLeft } from 'react-icons/fa';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.mogym.ir';

const palette = {
  bgDark: '#0e1015',
  cardDark: '#1a1d2e',
  borderDark: '#273043',
  textDark: '#ffffff',
  subDark: '#cbd5e1',
  textLight: '#0f172a',
  subLight: '#475569',
  primary: '#2563eb',
  error: '#ef4444',
};

const REJECT_REASON_UI = {
  10: { title: 'این برنامه تمرینی ندارد', desc: 'درخواست شما مربوط به تمرین بود، اما این پلن فقط غذایی است.' },
  11: { title: 'این برنامه غذایی ندارد', desc: 'درخواست شما مربوط به تغذیه بود، اما این پلن فقط تمرینی است.' },
  12: { title: 'نیاز به پلن پیشرفته‌تر دارد', desc: 'این نوع تغییرات در این سطح پلن قابل انجام نیست.' },
  13: { title: 'برای این نوع پلن قابل انجام نیست', desc: 'درخواست شما با شرایط این پلن هم‌خوانی ندارد.' },

  20: { title: 'درخواست نامرتبط با فیتنس', desc: 'درخواست باید مربوط به تمرین یا تغذیه همین برنامه باشد.' },
  21: { title: 'درخواست پزشکی مجاز نیست', desc: 'ما نمی‌توانیم نسخه/درمان پزشکی ارائه کنیم. اگر مشکل پزشکی دارید با پزشک مشورت کنید.' },
  22: { title: 'محتوای نامناسب', desc: 'درخواست شامل محتوای نامناسب بوده و قابل بررسی نیست.' },
  23: { title: 'متن نامفهوم یا اسپم', desc: 'متن ارسال‌شده قابل فهم نبود. لطفاً دقیق و شفاف بنویسید.' },
  24: { title: 'متن خیلی کوتاه یا مبهم', desc: 'برای اصلاح، توضیح دقیق لازم است (مثلاً درد زانو، تجهیزات، زمان تمرین).' },
  25: { title: 'تکراری یا قبلاً انجام شده', desc: 'این درخواست قبلاً ثبت/بررسی شده است.' },

  30: { title: 'تعداد درخواست‌ها زیاد است', desc: 'برای جلوگیری از سوءاستفاده، فعلاً امکان ثبت مجدد ندارید.' },
  31: { title: 'خارج از بازه مجاز', desc: 'امکان اصلاح فقط تا ۲۴ ساعت پس از فعال‌سازی برنامه فعال است.' },
  32: { title: 'محدودیت زمانی فعال است', desc: 'لطفاً بعداً دوباره تلاش کنید.' },

  40: { title: 'نیاز به بررسی انسانی', desc: 'این مورد باید دستی بررسی شود. نتیجه از همین بخش اطلاع‌رسانی می‌شود.' },
  99: { title: 'نامشخص', desc: 'درخواست قابل پردازش نبود. لطفاً دوباره با جزئیات بیشتر ارسال کنید.' },
};

const formatDate = (dt) => {
  if (!dt) return '-';
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return '-';

  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');

  return `${y}/${m}/${day} - ${hh}:${mm}`;
};

export default function RevisionDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const planId =
    location?.state?.planId ||
    params?.planId ||
    new URLSearchParams(location.search).get('planId') ||
    '';

  const [dark, setDark] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const colors = useMemo(
    () => ({
      bg: dark ? palette.bgDark : '#ffffff',
      card: dark ? palette.cardDark : '#ffffff',
      border: dark ? palette.borderDark : '#e5e7eb',
      text: dark ? palette.textDark : palette.textLight,
      sub: dark ? palette.subDark : palette.subLight,
      headerBtnBg: dark ? '#141827' : '#e5e7eb',
    }),
    [dark]
  );

  useEffect(() => {
    try {
      const stored = localStorage.getItem('theme.dark');
      if (stored !== null) setDark(stored === 'true');
    } catch {}
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError('');

        if (!planId) {
          setError('شناسه برنامه نامعتبر است.');
          return;
        }

        const token = localStorage.getItem('token');
        if (!token) {
          setError('ابتدا وارد حساب کاربری شوید.');
          return;
        }

        const res = await fetch(`${API_URL}/api/revision/plan/${planId}/latest`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const json = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(json?.Message || 'خطا در دریافت اطلاعات');
        }

        if (!cancelled) {
          setData(json?.result || json?.Result || null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'خطا در دریافت اطلاعات');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [planId]);

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    try {
      localStorage.setItem('theme.dark', next ? 'true' : 'false');
    } catch {}
  };

  if (loading) {
    return (
      <div className={dark ? 'dark' : ''}>
        <div
          className="min-h-screen flex flex-col items-center justify-center font-vazir"
          style={{
            backgroundColor: colors.bg,
            color: colors.text,
            direction: 'rtl',
          }}
        >
          <div
            className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin"
            style={{
              borderColor: palette.primary,
              borderTopColor: 'transparent',
            }}
          />
          <p className="mt-3 text-sm" style={{ color: colors.sub }}>
            در حال دریافت...
          </p>
        </div>
      </div>
    );
  }

  const rev = data?.revision;
  const reason = rev?.reasonCode != null ? REJECT_REASON_UI[rev.reasonCode] : null;

  return (
    <div className={dark ? 'dark' : ''}>
      <div
        className="min-h-screen font-vazir"
        style={{
          backgroundColor: colors.bg,
          color: colors.text,
          direction: 'rtl',
        }}
      >
        <div className="max-w-3xl mx-auto px-4 py-6">
          {/* Header */}
          <div className="flex items-center justify-between py-2 mb-4">
            <h1 className="text-lg md:text-xl font-bold">درخواست اصلاح</h1>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={toggleTheme}
                className="p-2 rounded-full border transition"
                style={{
                  backgroundColor: colors.headerBtnBg,
                  borderColor: colors.border,
                }}
                aria-label="تغییر تم"
              >
                {dark ? (
                  <FaSun size={18} color="#fde047" />
                ) : (
                  <FaMoon size={18} color="#0f172a" />
                )}
              </button>

              <button
                type="button"
                onClick={() => navigate(-1)}
                className="p-2 rounded-full border transition"
                style={{
                  backgroundColor: colors.headerBtnBg,
                  borderColor: colors.border,
                }}
                aria-label="بازگشت"
              >
                <FaArrowLeft size={18} color={dark ? '#fff' : '#0f172a'} />
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {error ? (
              <p
                className="text-sm text-right"
                style={{ color: palette.error }}
              >
                {error}
              </p>
            ) : null}

            {/* Reason */}
            <div
              className="rounded-2xl border p-4"
              style={{
                backgroundColor: colors.card,
                borderColor: colors.border,
              }}
            >
              <h2
                className="font-bold text-right mb-2"
                style={{ color: colors.text }}
              >
                نتیجه بررسی
              </h2>

              <p
                className="text-sm text-right leading-8"
                style={{ color: colors.sub }}
              >
                {reason?.title || 'این درخواست رد شده است.'}
              </p>

              {reason?.desc ? (
                <p
                  className="mt-2 text-sm text-right leading-8"
                  style={{ color: colors.sub }}
                >
                  {reason.desc}
                </p>
              ) : null}

              <p
                className="mt-3 text-sm text-right leading-8"
                style={{ color: colors.sub }}
              >
                برنامه فعلی شما همچنان قابل استفاده است.
              </p>
            </div>

            {/* User message */}
            <div
              className="rounded-2xl border p-4"
              style={{
                backgroundColor: colors.card,
                borderColor: colors.border,
              }}
            >
              <h2
                className="font-bold text-right mb-2"
                style={{ color: colors.text }}
              >
                متن درخواست شما
              </h2>

              <p
                className="text-sm text-right leading-8 whitespace-pre-line"
                style={{ color: colors.sub }}
              >
                {rev?.userMessage || '-'}
              </p>
            </div>

            {/* Admin response */}
            <div
              className="rounded-2xl border p-4"
              style={{
                backgroundColor: colors.card,
                borderColor: colors.border,
              }}
            >
              <h2
                className="font-bold text-right mb-2"
                style={{ color: colors.text }}
              >
                پاسخ / توضیح
              </h2>

              <p
                className="text-sm text-right leading-8 whitespace-pre-line"
                style={{ color: colors.sub }}
              >
                {rev?.responseMessage || '—'}
              </p>
            </div>

            {/* Optional meta */}
            {rev ? (
              <div
                className="rounded-2xl border p-4"
                style={{
                  backgroundColor: colors.card,
                  borderColor: colors.border,
                }}
              >
                <h2
                  className="font-bold text-right mb-3"
                  style={{ color: colors.text }}
                >
                  جزئیات
                </h2>

                <div className="space-y-2 text-sm" style={{ color: colors.sub }}>
                  <div className="flex justify-between gap-4">
                    <span>{formatDate(rev?.createdAt)}</span>
                    <span>زمان ثبت</span>
                  </div>

                  <div className="flex justify-between gap-4">
                    <span>{rev?.reasonCode ?? '-'}</span>
                    <span>کد دلیل</span>
                  </div>

                  <div className="flex justify-between gap-4">
                    <span>{rev?.status || '-'}</span>
                    <span>وضعیت</span>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}